import { useCallback, useEffect, useState } from "react";

import { db } from "@/utils/firebase";

interface Report {
  id: string;
  movieTitle: string;
  movieId: string;
  movieYear: string;
  moviePoster: string;
  reason: string;
  description: string;
  contactEmail: string;
  timestamp: string;
  status: "pending" | "reviewed" | "resolved";
}

interface FirestoreDoc {
  id: string;
  data: () => any;
}

interface FirestoreCollection {
  get: () => Promise<{ docs: FirestoreDoc[] }>;
  add: (data: any) => Promise<{ id: string }>;
}

interface MockFirestore {
  collection: (name: string) => FirestoreCollection;
}

// Mock Firestore for development
const mockFirestore: MockFirestore = {
  collection: (collectionName: string) => ({
    add: async (data: any) => {
      const reports = JSON.parse(localStorage.getItem('reports') || '[]');
      const newReport = {
        id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...data,
        timestamp: new Date().toISOString(),
        status: "pending" as const
      };
      
      reports.push(newReport);
      localStorage.setItem('reports', JSON.stringify(reports));
      
      // Real-time notification (could be enhanced with actual notifications)
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('Report Submitted', {
          body: 'Your report has been submitted successfully.',
          icon: '/favicon.ico'
        });
        
        // Close notification after 3 seconds
        setTimeout(() => notification.close(), 3000);
      }
      
      return { id: newReport.id };
    },
    get: async () => {
      const reports = JSON.parse(localStorage.getItem('reports') || '[]');
      return { docs: reports.map((r: Report) => ({ id: r.id, data: () => r })) };
    }
  })
};

export const useReports = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReports = async (): Promise<Report[]> => {
    try {
      const reportsCollection = db?.collection("reports") || mockFirestore.collection("reports");
      const snapshot = await reportsCollection.get();
      return snapshot.docs.map((doc: FirestoreDoc) => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
      console.error("Error fetching reports:", err);
      return [];
    }
  };

  const submitReport = async (reportData: Omit<Report, 'id' | 'timestamp' | 'status'>) => {
    setLoading(true);
    try {
      const reportsCollection = db?.collection("reports") || mockFirestore.collection("reports");
      await reportsCollection.add({
        ...reportData,
        timestamp: new Date().toISOString(),
        status: "pending"
      });
      
      // Refresh reports list
      const updatedReports = await fetchReports();
      setReports(updatedReports);
      
      return true;
    } catch (err) {
      console.error("Error submitting report:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshReports = useCallback(async () => {
    const updatedReports = await fetchReports();
    setReports(updatedReports);
  }, []);

  useEffect(() => {
    refreshReports();
  }, [refreshReports]);

  return {
    reports,
    loading,
    submitReport,
    refreshReports,
    fetchReports
  };
};